import { FC, PropsWithChildren } from 'react';
import { matchPath, Navigate, useLocation } from 'react-router-dom';

import { RoleEnum } from '@/api/rest/resources/types/membership';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useIsOrganizationOnboarded } from '@/hooks/useIsOrganizationOnboarded';
import { usePathSet } from '@/hooks/usePathSet';
import { useUser } from '@/lib/auth';
import { useUserOrganizationId } from '@/pages/shared/hooks/useUserOrganizationId';
import { ONBOARDING_FORM_PAGES } from '@/pages/shared/organization/pages/Onboarding.page';
import { buildPath } from '@/utils/buildPath';

export const RequireOnboarding: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const isAdmin = user.membership.role === RoleEnum.admin;
  const isStaff = user.is_staff;

  if (!isAdmin || isStaff) {
    // Do not force / block non-admin users when admins have not yet completed the onboarding
    return <>{children}</>;
  }
  return <Onboarding>{children}</Onboarding>;
};

const Onboarding: FC<PropsWithChildren> = ({ children }) => {
  const pathsSet = usePathSet();
  const organizationId = useUserOrganizationId();
  const { pathname } = useLocation();
  const isOrganizationOnboarded = useIsOrganizationOnboarded();
  const isConservation = useIsConservation();

  if (isConservation) return <>{children}</>;

  const onboardingPath = buildPath(pathsSet.organizationOnboarding, {
    pathParams: {
      organizationId,
      onboardingSection: 1,
    },
  });

  const onboardingFormPagesPaths = ONBOARDING_FORM_PAGES.map((onboardingSection) =>
    buildPath(pathsSet.organizationOnboarding, {
      pathParams: {
        organizationId,
        onboardingSection,
      },
    }),
  );

  const isOnboardingFormPage = onboardingFormPagesPaths.find((onboardingFormPage) =>
    matchPath(onboardingFormPage, pathname),
  );

  if (!isOrganizationOnboarded && !isOnboardingFormPage) {
    return <Navigate to={onboardingPath} replace />;
  }

  return <>{children}</>;
};
